import { useAuth0 } from '@auth0/auth0-react';
import { ContentBlock, Loader, Stack, Text } from 'braid-design-system';
import { format } from 'date-fns/format';
import { useMemo } from 'react';

import { LoginLabel } from './components/LoginLabel/LoginLabel.tsx';
import { SecurityBreachForm } from './components/SecurityBreachForm/SecurityBreachForm.tsx';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';

export const DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

export const PRODUCTS: Record<Product, { displayName: string }> = {
  'Job posting': { displayName: 'Job Posting' },
  'SEEK Optimised Apply': { displayName: 'SEEK Optimised Apply' },
  'Apply with SEEK': { displayName: 'Apply with SEEK' },
  'Partner system': { displayName: 'Partner system' },
  Other: { displayName: 'Other/Unknown' },
};

export type Product =
  | 'Job posting'
  | 'SEEK Optimised Apply'
  | 'Apply with SEEK'
  | 'Partner system'
  | 'Other';

export interface FormValues {
  dataType: string;
  usersImpacted: string;
  startDate: string;
  resolvedDate?: string;
  actionTaken: 'Yes' | 'No';
  actions: string;
  productsImpacted: Product[];
  otherProduct: string;
  extraDetails: string;
  seekData: 'Yes' | 'No';
  seekCredentials: 'Yes' | 'No';
  seekFault: 'Yes' | 'No';
  partnerName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactHours: string;
  disclosure: string;
}

export const SecurityBreach = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const { user, isLoading } = useAuth0();

  const initialValues = useMemo(
    (): FormValues => ({
      dataType: '',
      usersImpacted: '',
      startDate: format(new Date().setHours(0, 0, 0, 0), DATE_FORMAT),
      resolvedDate: undefined,
      actionTaken: 'No',
      actions: '',
      productsImpacted: [],
      otherProduct: '',
      extraDetails: '',
      seekData: 'No',
      seekCredentials: 'No',
      seekFault: 'No',
      partnerName: '',
      contactName: user?.name ?? '',
      contactEmail: user?.email ?? '',
      contactPhone: '',
      contactHours: '',
      disclosure: '',
    }),
    [user],
  );
  const loggedInUser = useMemo(() => user, [user]);

  return (
    <BasePage title="Report a security incident" breadcrumbs={breadcrumbs}>
      <Stack space="small">
        <Text tone="secondary">
          Use this form to report any security incidents that could potentially
          impact our organisation, even if the impact is not yet confirmed.
        </Text>
        <Text tone="secondary">
          This includes, but is not limited to, incidents such as data breaches,
          stolen API credentials, compromised systems, or unauthorized access to
          data.
        </Text>
        <Text tone="secondary">
          If you’re experiencing a security incident and are unsure whether it
          affects us, we still want to know.
        </Text>
      </Stack>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {!user ? <LoginLabel redirect="/manage/security-incident" /> : null}
          <ContentBlock width="small" align="left">
            <SecurityBreachForm
              defaultValues={initialValues}
              user={loggedInUser}
            />
          </ContentBlock>
        </>
      )}
    </BasePage>
  );
};
